import { usePrevious } from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { useGetWebcastViewerQuery } from '../../generated/graphql-viewer';
import { findViewerSingleSignOnPolicy } from '../../utils/graphql-helpers';
import { parseJwt } from '../../utils/parse-jwt';
import { setSessionStorageItem } from '../../utils/session-storage';
import { setUserInformation } from '../../utils/user-information-utils';

const tokenUri = process.env.REACT_APP_SSO_KEYCLOAK_TOKEN_URI || '';
const clientId = process.env.REACT_APP_AUTHENTICATION_KEYCLOAK_CLIENT_ID || '';

export function useSsoAuthentication() {
  const { webcastId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, refetch } = useGetWebcastViewerQuery({ variables: { webcastId }, skip: !webcastId });
  const authCode = searchParams.get('code');
  const [accessTokenAvailable, setAccessTokenAvailable] = useState(false);
  const [ssoAccessDenied, setSsoAccessDenied] = useState<boolean>();

  const [applicationAccessGranted, setApplicationAccessGranted] = useState<boolean>();
  const previousAccessGranted = usePrevious(applicationAccessGranted);

  const singleSignOnPolicy = findViewerSingleSignOnPolicy(data?.webcast?.securityPolicies);
  const ssoAccessGranted = singleSignOnPolicy?.accessGranted;
  const idp = singleSignOnPolicy?.idps?.[0];
  const redirectUri = window.location.href.split('?')[0];

  const timeoutRef = useRef(0);

  const redirectToKeycloak = useCallback(() => {
    const redirectUrl = window.location.href.split('?')[0];

    window.location.href = `${process.env.REACT_APP_SSO_KEYCLOAK_URI}&redirect_uri=${encodeURIComponent(
      redirectUrl
    )}&kc_idp_hint=${idp}`;
  }, [idp]);

  const getToken = useCallback(async () => {
    const refreshTokens = async (refreshToken: string) => {
      const refreshTokenResponse = await fetch(tokenUri, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `grant_type=refresh_token&refresh_token=${refreshToken}&client_id=${clientId}`,
      });

      if (!refreshTokenResponse.ok) return;

      const { access_token, refresh_token, expires_in } = await refreshTokenResponse.json();

      setSessionStorageItem('viewerAccessToken', access_token);
      startCheckingTokenExpiration(refresh_token, expires_in);
    };

    const startCheckingTokenExpiration = (refreshToken: string, expiresIn: number) => {
      const expirationTime = expiresIn * 1000;
      const delayBeforeExpiration = 2000;
      const difference = Date.now() + expirationTime - delayBeforeExpiration;
      const timeout = Math.max(difference - Date.now(), 0);

      timeoutRef.current = window.setTimeout(() => refreshTokens(refreshToken), timeout);
    };

    const tokenResponse = await fetch(tokenUri, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `grant_type=authorization_code&code=${authCode}&client_id=${clientId}&redirect_uri=${redirectUri}`,
    });

    if (!tokenResponse.ok) return redirectToKeycloak();

    const { access_token, refresh_token, expires_in } = await tokenResponse.json();

    setSessionStorageItem('viewerAccessToken', access_token);
    startCheckingTokenExpiration(refresh_token, expires_in);
    setAccessTokenAvailable(true);

    const userInfoJwt = parseJwt(access_token);
    if (userInfoJwt?.name) setUserInformation(webcastId, { nickname: userInfoJwt.name, email: userInfoJwt.email });

    const { data } = await refetch();

    if (data.webcast?.securityPolicies?.[0]?.accessGranted === false) {
      setSsoAccessDenied(true);
    }
  }, [authCode, redirectUri, redirectToKeycloak, webcastId, refetch]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  useEffect(() => {
    if (loading) return;

    if (ssoAccessGranted || !singleSignOnPolicy) setApplicationAccessGranted(true);
    if (accessTokenAvailable && ssoAccessGranted === false) setApplicationAccessGranted(false);
  }, [accessTokenAvailable, loading, singleSignOnPolicy, ssoAccessGranted]);

  useEffect(() => {
    if (applicationAccessGranted !== undefined) return;

    if (ssoAccessGranted === false)
      if (authCode) getToken();
      else redirectToKeycloak();
  }, [applicationAccessGranted, authCode, getToken, redirectToKeycloak, ssoAccessGranted]);

  useEffect(() => {
    if (previousAccessGranted === undefined && applicationAccessGranted !== undefined) {
      searchParams.delete('code');
      searchParams.delete('session_state');

      setSearchParams(searchParams);
    }
  }, [applicationAccessGranted, previousAccessGranted, searchParams, setSearchParams]);

  return { applicationAccessGranted, ssoAccessDenied };
}
